import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2,
  Users,
  Stethoscope,
  ShieldCheck,
  BadgeCheck,
  Wallet,
  ClipboardList,
  FileText,
  MessageCircle,
  TrendingUp,
  FileSignature,
  MapPin,
  Activity,
  AlertTriangle,
  XCircle,
  ArrowUpRight,
} from "lucide-react";

export default function AdminAnaliticas() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/analiticas");
        setData(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading || !data) {
    return (
      <div className="text-sm text-muted-foreground flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" /> Cargando analíticas…
      </div>
    );
  }

  const u = data.usuarios;
  const c = data.consultas;
  const s = data.suscripciones;
  const l = data.laboratorios;
  const acu = data.acuerdo || {};
  const ubi = data.ubicaciones || {};

  return (
    <div className="space-y-6" data-testid="admin-analiticas">
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          icon={<Users size={16} className="text-[#2563EB]" />}
          label="Personas usuarias"
          value={u.total_pacientes}
          sub={`${u.total_activos} activos · ${u.total_desactivados} desactivados`}
          testid="stat-pacientes"
        />
        <StatCard
          icon={<Stethoscope size={16} className="text-[#2563EB]" />}
          label="Médicos"
          value={u.total_medicos}
          sub={`${s.activas_por_plan.mensual + s.activas_por_plan.anual + s.activas_por_plan.comped_seed} en directorio`}
          testid="stat-medicos"
        />
        <StatCard
          icon={<Activity size={16} className="text-[#2563EB]" />}
          label="Activos (login)"
          value={u.activos_30d ?? 0}
          sub={`Últimos 30 d · ${u.activos_7d ?? 0} en 7 d`}
          testid="stat-activos"
        />
        <StatCard
          icon={<FileText size={16} className="text-[#2563EB]" />}
          label="Laboratorios"
          value={l.total}
          sub="PDFs cifrados"
          testid="stat-labs"
        />
      </section>

      <section className="rounded-xl border border-border bg-white p-4" data-testid="card-whatsapp">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-widest font-semibold text-[#2563EB] flex items-center gap-1">
              <MessageCircle size={12} /> Clicks a WhatsApp (directorio)
            </div>
            <div className="mt-1 font-display text-2xl font-semibold text-[#0F2942] tabular-nums">
              {(data.whatsapp?.total_clicks ?? 0).toLocaleString("es-MX")}
            </div>
          </div>
          <div className="text-[11px] text-muted-foreground text-right max-w-xs">
            Contacto directo persona usuaria → médico desde la ficha pública. Dedupe por (perfil, IP+UA) cada 5 min.
          </div>
        </div>
      </section>

      <section className="grid lg:grid-cols-3 gap-3">
        {/* Ingresos consulta */}
        <div
          className="lg:col-span-1 rounded-xl border border-border bg-white p-5"
          data-testid="card-ingresos"
        >
          <div className="text-[11px] font-semibold uppercase tracking-widest text-[#2563EB] flex items-center gap-1">
            <Wallet size={12} /> Ingresos consultas
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="font-display text-3xl font-semibold text-[#0F2942] tabular-nums">
              ${c.ingresos_mxn.toLocaleString("es-MX")}
            </span>
            <span className="text-sm text-muted-foreground">MXN</span>
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {c.pagadas} consultas pagadas · $39 c/u
          </div>
          <div className="mt-4 pt-4 border-t border-border text-xs text-muted-foreground">
            <div className="flex justify-between">
              <span>Iniciadas</span>
              <span className="tabular-nums text-foreground">{c.iniciadas ?? c.total}</span>
            </div>
            <div className="flex justify-between mt-1">
              <span>Pagadas</span>
              <span className="tabular-nums text-foreground">{c.pagadas}</span>
            </div>
            <div className="flex justify-between mt-1">
              <span>Requiere info</span>
              <span className="tabular-nums text-foreground">{c.requiere_info ?? 0}</span>
            </div>
            <div className="flex justify-between mt-1 pt-1 border-t border-border/60">
              <span className="font-semibold text-foreground">Conversión</span>
              <span className="tabular-nums font-semibold text-[#2563EB]">
                {(c.tasa_conversion_pct ?? 0).toFixed(1)}%
              </span>
            </div>
          </div>
          <Link
            to="/admin/consultas"
            className="mt-4 inline-flex items-center gap-1 text-[11px] font-semibold text-[#2563EB] hover:underline"
            data-testid="link-ver-todas-consultas"
          >
            Ver todas las consultas <ArrowUpRight size={12} />
          </Link>
        </div>

        {/* MRR */}
        <div
          className="lg:col-span-1 rounded-xl border border-border bg-white p-5"
          data-testid="card-mrr"
        >
          <div className="text-[11px] font-semibold uppercase tracking-widest text-[#2563EB] flex items-center gap-1">
            <TrendingUp size={12} /> MRR estimado
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="font-display text-3xl font-semibold text-[#0F2942] tabular-nums">
              ${s.mrr_estimado_mxn.toLocaleString("es-MX")}
            </span>
            <span className="text-sm text-muted-foreground">MXN / mes</span>
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            Anualizado ${s.anualizado_mxn.toLocaleString("es-MX")} · excluye comped
          </div>
          <div className="mt-4 pt-4 border-t border-border text-xs">
            <RowKV k="Mensual $69/mes" v={s.activas_por_plan.mensual} />
            <RowKV k="Anual $699/año" v={s.activas_por_plan.anual} />
            <RowKV k="Comped (demo)" v={s.activas_por_plan.comped_seed} />
            <div className="flex justify-between mt-2 pt-2 border-t border-border/60">
              <span className="text-muted-foreground inline-flex items-center gap-1">
                <XCircle size={11} className="text-amber-600" /> Cancelan al fin de periodo
              </span>
              <span className="tabular-nums text-foreground font-semibold">
                {s.cancelaciones_fin_periodo ?? 0}
              </span>
            </div>
          </div>
        </div>

        {/* Sub status */}
        <div
          className="lg:col-span-1 rounded-xl border border-border bg-white p-5"
          data-testid="card-subs-status"
        >
          <div className="text-[11px] font-semibold uppercase tracking-widest text-[#2563EB] flex items-center gap-1">
            <BadgeCheck size={12} /> Suscripciones por status
          </div>
          <div className="mt-3 space-y-2 text-sm">
            {Object.entries(s.por_status).map(([k, v]) => (
              <div
                key={k}
                className="flex items-center justify-between rounded-md border border-border bg-muted/40 px-3 py-2"
              >
                <span className="capitalize text-foreground text-xs">
                  {k.replace("_", " ")}
                </span>
                <span className="tabular-nums font-semibold text-[#0F2942]">{v}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Acuerdo + Ubicaciones (Fase 6) */}
      <section className="grid lg:grid-cols-2 gap-3">
        <AcuerdoCard acu={acu} />
        <UbicacionesCard ubi={ubi} />
      </section>

      <section className="grid lg:grid-cols-2 gap-3">
        <SeriesCard
          title="Registros nuevos"
          series={data.serie_30d.registros}
          testid="series-registros"
        />
        <SeriesCard
          title="Consultas creadas"
          series={data.serie_30d.consultas}
          testid="series-consultas"
        />
      </section>

      <section className="rounded-xl border border-border bg-white p-5" data-testid="card-mrr-note">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-[#2563EB]">
          <ClipboardList size={12} className="inline mr-1 -mt-0.5" /> Metodología
        </div>
        <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
          MRR se calcula como Σ(mensual × 69) + Σ(anual × 699/12). Se excluyen las suscripciones
          marcadas como <span className="font-mono">comped_seed</span> (demo). El total de
          consultas incluye todas las creadas independientemente del estado. Los ingresos por
          consultas cuentan sólo <span className="font-mono">payment_status=&quot;paid&quot;</span>.
        </p>
      </section>
    </div>
  );
}

function StatCard({ icon, label, value, sub, testid }) {
  return (
    <div className="rounded-xl border border-border bg-white p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1">
        {icon} {label}
      </div>
      <div className="mt-2 font-display text-3xl font-semibold text-[#0F2942] tabular-nums">
        {value}
      </div>
      <div className="text-[11px] text-muted-foreground mt-1">{sub}</div>
    </div>
  );
}

function RowKV({ k, v }) {
  return (
    <div className="flex justify-between mt-1">
      <span className="text-muted-foreground">{k}</span>
      <span className="tabular-nums text-foreground">{v}</span>
    </div>
  );
}

function AcuerdoCard({ acu }) {
  const pct = acu.total_usuarios
    ? Math.round((acu.total_firmas / acu.total_usuarios) * 100)
    : 0;
  return (
    <div className="rounded-xl border border-border bg-white p-5" data-testid="card-acuerdo">
      <div className="text-[11px] font-semibold uppercase tracking-widest text-[#2563EB] flex items-center gap-1">
        <FileSignature size={12} /> Firmas del Acuerdo · v{acu.version_actual || "1.0"}
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="font-display text-3xl font-semibold text-[#0F2942] tabular-nums">
          {acu.total_firmas ?? 0}
        </span>
        <span className="text-sm text-muted-foreground">/ {acu.total_usuarios ?? 0}</span>
      </div>
      <div className="mt-1 text-xs text-muted-foreground">
        Aceptaciones firmadas de la versión vigente
      </div>
      <div className="mt-3 h-1.5 rounded-full bg-muted overflow-hidden">
        <div
          className="h-full bg-[#2563EB] transition-all"
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </div>
      <div className="mt-3 pt-3 border-t border-border text-xs">
        <div className="flex justify-between">
          <span className="text-muted-foreground">Pendientes de firma</span>
          <span
            className={`tabular-nums font-semibold ${
              (acu.pendientes ?? 0) > 0 ? "text-amber-700" : "text-emerald-700"
            }`}
            data-testid="acuerdo-pendientes"
          >
            {acu.pendientes ?? 0}
          </span>
        </div>
        <div className="flex justify-between mt-1">
          <span className="text-muted-foreground">% aceptación</span>
          <span className="tabular-nums font-semibold text-foreground">{pct}%</span>
        </div>
      </div>
    </div>
  );
}

function UbicacionesCard({ ubi }) {
  const disponible = ubi.disponible !== false;
  const pais = ubi.por_pais || [];
  const ciudad = ubi.por_ciudad || [];
  return (
    <div className="rounded-xl border border-border bg-white p-5" data-testid="card-ubicaciones">
      <div className="text-[11px] font-semibold uppercase tracking-widest text-[#2563EB] flex items-center gap-1">
        <MapPin size={12} /> Accesos por ubicación
      </div>
      {!disponible ? (
        <div
          className="mt-3 text-xs text-muted-foreground flex items-center gap-1 rounded-md border border-dashed border-border bg-muted/30 p-3"
          data-testid="ubicaciones-no-disponible"
        >
          <AlertTriangle size={12} /> Geolocalización no disponible por ahora. Los datos volverán en la próxima actualización.
        </div>
      ) : pais.length === 0 && ciudad.length === 0 ? (
        <div className="mt-3 text-xs text-muted-foreground rounded-md border border-dashed border-border bg-muted/30 p-3">
          Aún no hay suficientes accesos con IP pública para geolocalizar.
        </div>
      ) : (
        <div className="mt-3 grid sm:grid-cols-2 gap-4 text-xs">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">
              Por país
            </div>
            <ul className="space-y-1">
              {pais.slice(0, 6).map((p) => (
                <li
                  key={p.country}
                  className="flex justify-between rounded-md border border-border bg-muted/30 px-2 py-1"
                >
                  <span className="text-foreground">{p.country}</span>
                  <span className="tabular-nums font-semibold text-[#0F2942]">{p.count}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">
              Top ciudades
            </div>
            <ul className="space-y-1">
              {ciudad.slice(0, 6).map((c, i) => (
                <li
                  key={`${c.city}-${i}`}
                  className="flex justify-between rounded-md border border-border bg-muted/30 px-2 py-1"
                >
                  <span className="text-foreground truncate">
                    {c.city}
                    <span className="text-muted-foreground"> · {c.country}</span>
                  </span>
                  <span className="tabular-nums font-semibold text-[#0F2942] ml-2">
                    {c.count}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
      {(ubi.sin_geo ?? 0) > 0 && (
        <div className="mt-2 text-[10px] text-muted-foreground">
          {ubi.sin_geo} accesos sin geolocalización (IP privada o falla del proveedor).
        </div>
      )}
    </div>
  );
}

function SeriesCard({ title, series, testid }) {
  const max = Math.max(1, ...series.map((s) => s.count));
  return (
    <div className="rounded-xl border border-border bg-white p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-widest text-[#2563EB]">
            {title}
          </div>
          <div className="text-xs text-muted-foreground">Últimos 30 días</div>
        </div>
        <div className="text-xs text-muted-foreground">
          Total{" "}
          <span className="text-foreground font-semibold tabular-nums">
            {series.reduce((a, b) => a + b.count, 0)}
          </span>
        </div>
      </div>
      <div className="flex items-end gap-[3px] h-24">
        {series.map((s) => {
          const h = Math.max(2, Math.round((s.count / max) * 100));
          return (
            <div
              key={s.date}
              title={`${s.date}: ${s.count}`}
              className={`flex-1 rounded-t ${s.count > 0 ? "bg-[#2563EB]" : "bg-muted"} transition-colors`}
              style={{ height: `${h}%` }}
            />
          );
        })}
      </div>
      <div className="mt-2 flex justify-between text-[10px] text-muted-foreground font-mono">
        <span>{series[0]?.date.slice(5)}</span>
        <span>{series[series.length - 1]?.date.slice(5)}</span>
      </div>
    </div>
  );
}
